import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import {
  fetchCustomFields
} from 'actions/fields/fields.jsx'

import {
  createLayoutManagerForContentGrid, updateLayoutManagerForContentGrid, updateDynamicLayoutManager
} from 'actions/contentGrid';
import { updateWidgetEditorState } from 'actions/editor';
import {
  arrangeLayoutContents, removeLayoutContents,
  invalidateLayoutManagerMappings,
  fetchFilterMappingsForLayoutManager
} from 'actions/layoutManager';

import {
  Container, Modal, Button, Form, Dropdown
} from 'react-bootstrap';

import _ from 'lodash';
import cx from 'classnames';

import { EditorContext } from "components/Editor";
import { Footer, ModalHeader, getContentForFieldSelector, getFieldSelector } from '../components';
import ContentConfigCard from '../components/contentGatingCard';
import { ThumbnailControl } from '../components/thumbnailControl';
import StoryboardContext from "components/Storyboard/context";
import InfiniteScroll from "components/InfiniteScroll";
import { ContentCard, ContentCardPlaceholder } from '../components/contentCard';
import GatingConfigurations from 'components/gatings';

import styles from './dynamicSection.module.scss'
import { AssetFilter, PropertyContext } from '../../../../../AssetFilter';
import { guid } from 'services/util';
import { processRequest } from 'services/filters.service';
import { filterOptions, conditions, sortOptions, orderOptions } from 'constants/filters';

const DynamicSection = ({ show, className, widgetProps, onClose }) => {
  const dispatch = useDispatch();

  const container = React.createRef(null);

  const { id } = useParams();
  const widgetId = _.get(widgetProps, 'uid');

  const storyboardContext = React.useContext(StoryboardContext);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const layoutId = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.id`) || null);
  const filters = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.filters`) || []);
  const sort = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.sort`) || null);
  const order = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.order`) || null);

  const contents = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.items`) || []);
  const loading = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.loading`, false));
  const pagination = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.pagination`, {}));
  const contentCount = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.pagination.totalRecordsCount`, 0));
  const hasMoreItems = _.size(contents) > 0 && _.get(pagination, "current") < _.get(pagination, "totalPages") - 1;

  const updating = _.get(widgetProps, 'editorState.layout.updating', false);
  const updated = _.get(widgetProps, 'editorState.layout.updated', false);

  useEffect(() => {
    dispatch(fetchCustomFields(storyboardContext.id));

    return () => {
      dispatch(invalidateLayoutManagerMappings());
    }
  }, []);

  useEffect(() => {
    if (filters) {
      handleFetchMappings(0);
    }
  }, [filters, sort, order]);

  useEffect(() => {
    if (updated) {
      handleClose();
    }
  }, [updated])

  const handleFetchMappings = (page) => {
    var commandsRequest = processRequest(filters);
    commandsRequest = (commandsRequest || []).filter(item => item.operator === "IS_ALL" || item.value && item.value.length > 0);

    if (commandsRequest.length === 0) {
      var request = {
        items: []
      }

      dispatch(updateDynamicLayoutManager(editorSession, request));
      return;
    }

    var request = {
      storyboard: storyboardContext.id,
      commands: commandsRequest,
      sort: sort,
      order: order,
    }

    dispatch(fetchFilterMappingsForLayoutManager(editorSession, widgetId, layoutId, request, page, 20));
  }

  const loadMore = () => {
    const nextPage = pagination.current + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    handleFetchMappings(nextPage);
  }

  const handleSave = () => {
    var commandsRequest = processRequest(filters);
    commandsRequest = (commandsRequest || []).filter(item => item.operator === "IS_ALL" || item.value && item.value.length > 0);

    var layoutRequest = {
      page: id,
      storyboard: storyboardContext.id,
      commands: commandsRequest,
      sort: sort,
      order: order,
      type: 'DYNAMIC'
    }

    if (!layoutId) {
      dispatch(createLayoutManagerForContentGrid(editorSession, widgetProps.uid, layoutRequest));
    } else {
      dispatch(updateLayoutManagerForContentGrid(editorSession, widgetProps.uid, layoutId, layoutRequest));
    }
  }

  const handleClose = () => {
    const existingLayout = _.get(widgetProps, 'editorState.layout') | {};
    const nextState = {
      layout: {
        ...existingLayout,
        updated: false
      }
    }

    dispatch(invalidateLayoutManagerMappings());
    dispatch(updateWidgetEditorState(editorSession, widgetId, nextState));
    onClose();
  }

  return (
    <Modal show={show} className={styles.sectionManagerModal} backdropClassName={styles.backdrop} centered>
      <ModalHeader headerName={_.get(widgetProps, 'name')} type="dynamic" onClose={handleClose} />
      <Modal.Body className={styles.modalBody}>
        <Container fluid className={cx(styles.dynamicContainer, className)}>
          <section className={styles.assetGridLayout}>
            <FilterRenderer widget={widgetProps} />
            <div className={styles.scrollableContainer} ref={container}>
              <InfiniteScroll
                className={cx(styles.scrollable)}
                loading={loading}
                hasMore={hasMoreItems}
                loadMore={loadMore}
              >
                {contents.map((data, index) => {
                  return (
                    <ContentCard
                      key={_.get(data, 'content.id', null)}
                      assetData={data}
                    />
                  )
                })
                }
                {loading &&
                  _.times(_.size(contents) == 0 ? 4 : 7).map(index => {
                    return <ContentCardPlaceholder key={index} />
                  })
                }
              </InfiniteScroll>
            </div>
          </section>
          <section className={styles.assetEditorLayout}>
          </section>
        </Container>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Footer type={'dynamic'} filtered={_.size(contents) >0 ? contentCount : 0} selected={0} updating={updating} onSave={handleSave} />
      </Modal.Footer>
    </Modal>
  )
}

const FilterRenderer = ({ widget }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const storyboardContext = React.useContext(StoryboardContext);

  const layoutId = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.id`) || null);
  const filters = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.filters`) || []);
  const content = _.get(widget, 'content', {});

  const loading = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.loading`, false));

  const customFields = useSelector(state => _.get(state, 'fields.items', []));

  const propertiesOption = useMemo(() => {
    const customFieldProperties = customFields.map(field => {
      return {
        key: `custom_field_${field.id}`,
        value: {
          fieldId: field.id,
          type: 'CUSTOM_FIELD'
        },
        label: field.name,
        data_type: 'list',
        icon: 'list-field',
        config: {
          multiple: true,
          options: (field.options || []).map(option => {
            return {
              value: option.id,
              label: option.value
            }
          })
        }
      }
    })

    return [...filterOptions, ...customFieldProperties];
  })

  useEffect(() => {
    if (!layoutId && filters && filters.length === 0) {
      initiateLayout();
    }
  }, [])

  const initiateLayout = () => {
    var requestFilters = _.get(content, 'filters', []);

    if (!content || !content.filters || _.size(content.filters) <= 0) {
      requestFilters = [...filters, {
        property: {
          type: 'ASSET'
        },
        operator: 'IS'
      }];
    }

    requestFilters = requestFilters.map(item => {
      return {
        ...item,
        id: guid()
      }
    })

    var request = {
      ...content,
      filters: requestFilters,
      sort: _.get(content, 'sort', 'modified_date'),
      order: _.get(content, 'order', 'DESC')
    }

    dispatch(updateDynamicLayoutManager(editorSession, request));
  }

  const updateFilterChanges = (filterRequests) => {
    var request = {
      filters: filterRequests
    }

    dispatch(updateDynamicLayoutManager(editorSession, request));
  }

  const insertFilter = () => {
    var request = {
      id: guid(),
      property: {
        type: 'ASSET'
      },
      operator: 'IS'
    }

    var requestFilters = [...filters, request];

    updateFilterChanges(requestFilters);
  }

  const deleteFilter = (index) => {
    var updated = [...filters];
    updated.splice(index, 1);

    updateFilterChanges(updated);
  }

  const onFilterChange = (filter) => {
    var updated = filters.map((item, index) => {
      if (item.id === filter.id) {
        return filter;
      }

      return item;
    });

    updateFilterChanges(updated);
  }

  return (
    <Container fluid className={styles.dynamicFilterContainer}>
      <PropertyContext.Provider value={propertiesOption}>
        {filters.map((data, index) => {
          return (
            <AssetFilter
              key={index}
              index={index}
              value={data}
              onChange={onFilterChange}
              onInsert={insertFilter}
              onDelete={deleteFilter}
              canDelete={index !== 0}
              showAddFilter={index === (filters.length - 1)}
              filters={filters}
            />
          )
        })}
      </PropertyContext.Provider>
      <SortRender />
    </Container>
  )
}

const SortRender = () => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const sort = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.sort`) || {});
  const order = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.order`) || {});

  const sortOption = sortOptions.find(item => item.value === sort);
  const orderOption = orderOptions.find(item => item.value === order);

  const handleSortChange = (sort) => {
    var request = {
      sort: sort
    }
    dispatch(updateDynamicLayoutManager(editorSession, request));
  }

  const handleOrderChange = (order) => {
    var request = {
      order: order
    }
    dispatch(updateDynamicLayoutManager(editorSession, request));
  }

  const handleSortKeyDown = (event, data) => {
    if (event.key === 'Enter') {
      handleSortChange(data);
    }
  };

  const handleOrderKeyDown = (event, data) => {
    if (event.key === 'Enter') {
      handleOrderChange(data);
    }
  };

  return (
    <div className={styles.sortActionContainer}>
      <div className={styles.conditions}>
        <span className={styles.label}>
          <i className={cx(styles.sortIcon, 'cleverstory-sort')} />
          Sort
        </span>

        <Dropdown className={styles.sortDropdown} onSelect={handleSortChange}>
          <Dropdown.Toggle className={styles.selected}>
            <Button size='sm' variant='primary' className={styles.button}>
              <span className={styles.filterType}>
                {_.get(sortOption, 'label', '')}
              </span>
              <i className={cx(styles.icon, 'cleverstory-drop-down')} />
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.typesMenu}>
            {sortOptions.map(item => {
              return <Dropdown.Item
                eventKey={item.value}
                onKeyDown={(e) => handleSortKeyDown(e, item.value)}>
                {item.label}
              </Dropdown.Item>
            })}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className={styles.sortDropdown} onSelect={handleOrderChange}>
          <Dropdown.Toggle className={styles.selected}>
            <Button size='sm' variant='primary' className={styles.button}>
              <span className={styles.filterType}>
                {_.get(orderOption, 'label', '')}
              </span>
              <i className={cx(styles.icon, 'cleverstory-drop-down')} />
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.typesMenu}>
            {orderOptions.map(item => {
              return <Dropdown.Item
                eventKey={item.value}
                onKeyDown={(e) => handleOrderKeyDown(e, item.value)}>
                {item.label}
              </Dropdown.Item>
            })}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {/* <div className={styles.actionWrapper}>
        <Button size='sm' className={styles.cancelButton} variant='secondary'>Cancel</Button>
        <Button size='sm' className={styles.saveButton} variant='primary'>Save & Close</Button>
      </div> */}
    </div>
  )
}

function getConditionsForProperty(property) {
  if (!(property && property.data_type)) {
    return [];
  }

  const filter = conditions.find(item => {
    return item.type === property.data_type;
  });

  if (!filter) {
    return [];
  }

  return filter.options || [];
}

function getDefaultConditionForProperty(property) {
  const conditions = getConditionsForProperty(property);
  if (conditions.length == 0) {
    return '';
  }

  return conditions[0].key;
}

export default DynamicSection